// src/components/core/PageProvider.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import useApiInitializer from 'hooks/useApiInitializer';
import useApi from 'hooks/useApi';
import { createContext, useContext, useEffect, useMemo, useRef } from 'react';
import { useModuleContext } from './ModuleProvider';
import useZustand from 'hooks/useZustand';
import useUrl from 'hooks/useUrl';

// *******************************************************************************
// CONTEXT
// *******************************************************************************
const PageContext = createContext({});
export const usePageContext = () => useContext(PageContext);

// *******************************************************************************
// PROVIDER
// *******************************************************************************
const PageProvider = ({ children }) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const storedCurrentPage = useZustand('module', 'currentPage');
  const apiGlobal = useZustand('global', 'useApi');

  const { registerProviderContext } = useModuleContext();

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────
  const parsedUrl = useUrl();
  const { params } = parsedUrl;

  // Initialize API global state (registers get/post/put/del/upsert/done in global store)
  useApiInitializer();

  // Trigger auto-fetch if configured — most page layouts have autoFetch: false
  // and manage their own data fetching inside the page component
  const routingParamKey = storedCurrentPage?.routing?.paramKey;
  const paramKey = routingParamKey === undefined ? '_id' : routingParamKey;

  useApi({ hideLoading: false, paramKey, params });

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  const data = storedCurrentPage?.data;

  // Extract API methods from global store (registered by useApiInitializer)
  const refresh = apiGlobal?.refresh;
  const get = apiGlobal?.get;
  const post = apiGlobal?.post;
  const put = apiGlobal?.put;
  const del = apiGlobal?.del;
  const upsert = apiGlobal?.upsert;
  const done = apiGlobal?.done;

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  // (none — page layout has no editing state or tab management)

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────
  // Keep a ref to the latest exposedValues to avoid infinite loops in the registration effect
  const exposedValuesRef = useRef(null);

  // Register page context in ModuleProvider
  // Pattern mirrors DocumentProvider — intentionally excludes exposedValues from deps
  // to prevent infinite loops. Consumers use usePageContext() for reactive updates.
  useEffect(() => {
    registerProviderContext?.('pageContext', exposedValuesRef.current);

    return () => {
      registerProviderContext?.('pageContext', null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registerProviderContext]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  // (none — page layout has no local handlers; API calls go through apiGlobal)

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────
  const exposedValues = useMemo(
    () => ({
      pageContext: true,
      data,
      refresh,
      get,
      post,
      put,
      del,
      upsert,
      done,
      ...parsedUrl,
    }),
    [data, refresh, get, post, put, del, upsert, done, parsedUrl]
  );

  // Keep ref in sync for the registration effect
  exposedValuesRef.current = exposedValues;

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return <PageContext.Provider value={exposedValues}>{children}</PageContext.Provider>;
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default PageProvider;
